import { layerCategories } from '@/i18n/i18nStrings/layerCategories'
import { colorHex } from '@/utils/colors'
import {
  I18nString,
  IBaseDataLayer,
  IDataLayer,
  ILayerValue,
  ILoadedDataLayer,
  SimpleGeoJSONFeature,
  TDataLayerStyle,
  TLayerType
} from '@lifk/types'
import { isNumber, roundToSigDigits } from '@lifk/utils'
import { GeoJSONFeature } from 'mapbox-gl'

/*
  Data Layer getters
*/
export const getDataLayerCategory = (
  dataLayer: IBaseDataLayer
): { name: I18nString; color: string } => ({
  name: layerCategories[
    dataLayer.layerType.startsWith('metric') ? 'metric' : dataLayer.layerType
  ],
  color: colorHex('poppy')
})

export const getDataLayerByKey = (
  dataLayers: IDataLayer[] | undefined,
  key: string
) => (dataLayers ?? []).find((dl) => dl.key === key)

export const getDataLayerByTilesetLayer = (
  dataLayers: IDataLayer[] | undefined,
  tilesetLayer: string | undefined
) =>
  (dataLayers ?? []).find((dl) =>
    dl.mapLayers[0].layerValues.some((ml) => ml.tilesetLayer === tilesetLayer)
  )

export const getLayerValueByKey = (
  layerValues: ILayerValue[] | undefined,
  key: string
) => (layerValues ?? []).find((lv) => lv.key === key)

export const tilesetIdFromUrl = (
  tilesetUrl: string | undefined,
  includePrefix: boolean
) => {
  if (includePrefix) {
    return tilesetUrl?.replace('mapbox://', '') ?? ''
  }
  return tilesetUrl?.match(/(?<=\.prod-|\.stg-).*?(?=#|$)/)?.[0] ?? ''
}

type FeaturePropertyMatch = {
  featureValue: string | number | undefined
  isTargetAttr: boolean
  isFilterAttr: boolean
}

export const layerValueForValue = (
  layerValues: ILayerValue[],
  featureProperty: FeaturePropertyMatch,
  isGradient: boolean
): ILayerValue | undefined => {
  const { featureValue, isTargetAttr, isFilterAttr } = featureProperty
  if (isTargetAttr && isNumber(featureValue)) {
    // round to remove number artifacts (eg 1.00000000000000000002)
    const roundedFeatureValue = roundToSigDigits(featureValue as number, 5)

    const layerMin = Math.min(
      ...layerValues.map((lv) => lv.styleConfig.minValue ?? 0)
    )
    const layerMax = Math.max(
      ...layerValues.map((lv) => lv.styleConfig.maxValue ?? 0)
    )
    return layerValues
      .filter(
        (lv) =>
          !(isGradient && lv.styleConfig.minValue === lv.styleConfig.maxValue)
      ) // filter out min: 0, max: 0 lvs for gradients
      .find((lv) => {
        if (
          isNumber(lv.styleConfig.minValue) &&
          isNumber(lv.styleConfig.maxValue)
        ) {
          const min = lv.styleConfig.minValue as number
          const max = lv.styleConfig.maxValue as number

          // If less than the total min, default to min layer value
          if (roundedFeatureValue < layerMin) {
            return min === layerMin
          }
          // If more than the total max, default to max layer value
          if (roundedFeatureValue > layerMax) {
            return max === layerMax
          }
          return min <= roundedFeatureValue && roundedFeatureValue <= max
        }
      })
  } else if (isFilterAttr) {
    for (let i = 0; i < layerValues.length; i++) {
      // If the second item in the mapbox filter is an array (e.g., ['get', 'attr']), the third item is the value to match — otherwise, it's the other way around.
      const filter = layerValues[i].layerConfig.filter
      const selectedFilterExpression = Array.isArray(filter?.[1])
        ? filter?.[2]
        : filter?.[1]

      if (featureValue === selectedFilterExpression) {
        return layerValues[i]
      }
    }
  }
  return undefined
}

export const getFeatureValueFromProperties = (
  featureProperties: object,
  layerValues: ILayerValue[]
): FeaturePropertyMatch | undefined => {
  const featurePropertiesKeys = Object.keys(featureProperties)
  for (const layerValue of layerValues) {
    const targetAttr = layerValue.styleConfig?.targetAttribute

    const firstArrayInFilter = layerValue.layerConfig.filter?.find((filter) =>
      Array.isArray(filter)
    )
    const secondArrayInFilter = firstArrayInFilter?.find((filter) =>
      Array.isArray(filter)
    )
    const filterAttr = secondArrayInFilter
      ? secondArrayInFilter?.[1]
      : firstArrayInFilter?.[1]

    if (targetAttr && featurePropertiesKeys.includes(targetAttr)) {
      return {
        featureValue: targetAttr,
        isTargetAttr: true,
        isFilterAttr: false
      }
    }
    if (filterAttr && featurePropertiesKeys.includes(filterAttr)) {
      return {
        featureValue: filterAttr,
        isTargetAttr: false,
        isFilterAttr: true
      }
    }
  }
}

export const dataLayerValueForValue = (
  dataLayer: IBaseDataLayer,
  featureProperty: FeaturePropertyMatch
) =>
  layerValueForValue(
    dataLayer.mapLayers.flatMap(({ layerValues }) => layerValues),
    featureProperty,
    dataLayer.isGradient
  )

/*
  Mapbox feature -> Data Layer/Layer Value utils
*/
const getDataLayerKey = (feature: SimpleGeoJSONFeature): string | undefined => {
  if (!feature.layer?.id) return
  const split = feature.layer.id.split('.')
  if (split.length < 2) return // not a data layer
  return split[0]
}

export const getLayerValueKey = (
  feature: SimpleGeoJSONFeature
): string | undefined => {
  if (!feature.layer?.id) return
  const split = feature.layer.id.split('.')
  if (split.length < 2) return // not a data layer
  return split[1]
}

export const dataLayerFromFeature = (
  feature: SimpleGeoJSONFeature,
  loadedDataLayers: ILoadedDataLayer[]
): IDataLayer | undefined =>
  loadedDataLayers.find(
    ({ dataLayer }) => dataLayer.key === getDataLayerKey(feature)
  )?.dataLayer

export const layerValueFromFeature = (
  feature: SimpleGeoJSONFeature,
  loadedDataLayers: ILoadedDataLayer[]
) => {
  const layerValueKey = getLayerValueKey(feature)
  const dataLayer = dataLayerFromFeature(feature, loadedDataLayers)

  if (layerValueKey && dataLayer) {
    return dataLayer.mapLayers
      .flatMap(({ layerValues }) => layerValues)
      .find((layerValue) => layerValueKey == layerValue.key)
  }
  return undefined
}

export const isMuted = (
  key: string,
  feature: SimpleGeoJSONFeature,
  loadedDataLayers: ILoadedDataLayer[]
): boolean => {
  const layerValue = layerValueFromFeature(feature, loadedDataLayers)
  return layerValue?.muted?.includes(key) ?? false
}

/*
  Sort
*/
const layerTypeOrdering: TLayerType[] = [
  'metric (percentile)',
  'metric (units)',
  'boundary',
  'feature'
]
const styleOrdering: TDataLayerStyle[] = [
  'Raster',
  'Polygons',
  'Polygons (symbolized)',
  'Lines',
  'Points',
  'Animated',
  'Animated (particles)'
]
const getUniqueStyles = (dataLayer: IDataLayer): TDataLayerStyle[] =>
  Array.from(
    new Set(
      (dataLayer.mapLayers ?? []).flatMap((mapLayer) =>
        (mapLayer.layerValues ?? []).flatMap(({ style }) => style)
      )
    )
  ).sort((a, b) => styleOrdering.indexOf(a) - styleOrdering.indexOf(b))

export const sortDataLayers = (
  loaded: ILoadedDataLayer[]
): ILoadedDataLayer[] => {
  return loaded.sort((a, b) => {
    // sort by layer type
    if (a.dataLayer.layerType !== b.dataLayer.layerType) {
      const aTypeIndex = layerTypeOrdering.indexOf(a.dataLayer.layerType)
      const bTypeIndex = layerTypeOrdering.indexOf(b.dataLayer.layerType)
      return aTypeIndex - bTypeIndex
    }

    // within layer type, sort by layer value style
    const aStyles: TDataLayerStyle[] = getUniqueStyles(a.dataLayer)
    const bStyles: TDataLayerStyle[] = getUniqueStyles(b.dataLayer)
    // if no styles, move to the end (indicates missing layer values)
    if (!aStyles.length) return -1
    if (!bStyles.length) return 1
    // sort by front-most layer value style
    return (
      styleOrdering.indexOf(aStyles[aStyles.length - 1]) -
      styleOrdering.indexOf(bStyles[bStyles.length - 1])
    )
  })
}

export const sortLayerValuesByMinMax = (
  layerValues: ILayerValue[]
): ILayerValue[] =>
  layerValues.sort((a, b) => {
    if (a.styleConfig.minValue === b.styleConfig.minValue) {
      return (a.styleConfig.maxValue ?? 0) - (b.styleConfig.maxValue ?? 0)
    }
    return (a.styleConfig.minValue ?? 0) - (b.styleConfig.minValue ?? 0)
  })

/*
  GeoJSON Features
*/
export const getFeatureIds = (features?: GeoJSONFeature[]) =>
  features
    ?.map((feature) => feature.id?.toString() ?? '')
    .filter((id) => id.length)
